import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DURATION, EASE_OUT_EXPO } from '../../lib/motion';

/** Tiny classnames joiner — no runtime dependency for something this small. */
export function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

// ------------------------------------------------------------------ Button
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_BASE =
  'relative inline-flex max-w-full items-center justify-center gap-2 font-medium whitespace-nowrap rounded-full transition-[background,color,box-shadow,transform] duration-200 disabled:opacity-50 disabled:pointer-events-none select-none';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-clay-500 text-white shadow-[0_8px_24px_-8px_rgba(226,101,74,0.7)] hover:bg-clay-600 hover:shadow-[0_12px_32px_-8px_rgba(226,101,74,0.8)] active:scale-[0.98]',
  secondary:
    'bg-ink-900 text-sand-50 hover:bg-ink-800 active:scale-[0.98] dark:bg-sand-100 dark:text-ink-900',
  outline:
    'border border-[var(--border-subtle)] bg-transparent hover:bg-[var(--surface-sunken)] active:scale-[0.98]',
  ghost: 'bg-transparent hover:bg-[var(--surface-sunken)] active:scale-[0.98]',
  danger: 'bg-red-600 text-white hover:bg-red-700 active:scale-[0.98]',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-4 text-sm',
  md: 'h-11 px-6 text-[0.95rem]',
  lg: 'h-14 px-8 text-base',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
}

/** A label that ellipsizes rather than pushing the button past its container. */
function ButtonLabel({ children }: { children: ReactNode }) {
  if (children == null || children === false) return null;
  return <span className="min-w-0 truncate">{children}</span>;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cx(BUTTON_BASE, BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className)}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      <ButtonLabel>{children}</ButtonLabel>
    </button>
  );
});

export function ButtonLink({
  to,
  variant = 'primary',
  size = 'md',
  className,
  children,
  icon,
  external,
  state,
}: {
  to: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children: ReactNode;
  icon?: ReactNode;
  external?: boolean;
  /** Router state for the target page, e.g. where to return after signing in. */
  state?: unknown;
}) {
  const classes = cx(BUTTON_BASE, BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className);
  if (external) {
    return (
      <a href={to} className={classes} target="_blank" rel="noreferrer noopener">
        {icon}
        <ButtonLabel>{children}</ButtonLabel>
      </a>
    );
  }
  return (
    <Link to={to} state={state} className={classes}>
      {icon}
      <ButtonLabel>{children}</ButtonLabel>
    </Link>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Загрузка"
      className={cx(
        'inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent',
        className,
      )}
    />
  );
}

// ------------------------------------------------------------------- Inputs
const FIELD_BASE =
  'w-full rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-raised)] px-4 py-3 text-[0.95rem] outline-none transition-shadow placeholder:text-[var(--text-muted)] focus:border-clay-400 focus:ring-4 focus:ring-clay-500/12 disabled:opacity-60';

/**
 * Full width is the right default for a field, but it must not be the last
 * word.
 *
 * `cx()` concatenates rather than merges, and precedence between two classes
 * on one element is decided by their order in the *stylesheet*, not in the
 * attribute — and Tailwind emits `.w-full` after `.w-24`, `.w-28` and
 * `.w-auto`. So every `className="w-auto"` passed to a field here silently did
 * nothing, and a field sharing a flex row with one collapsed to its
 * min-content. Drop the base width when the caller brings their own.
 *
 * Matches `w-*` only at the start of a class, so `min-w-40` and `max-w-md`
 * (which do not conflict) still compose with the default.
 */
const OWN_WIDTH = /(?:^|\s)w-(?!full(?:\s|$))\S+/;
const fieldBase = (className?: string): string =>
  className && OWN_WIDTH.test(className) ? FIELD_BASE.replace('w-full ', '') : FIELD_BASE;

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cx(fieldBase(className), className)} {...rest} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        className={cx(fieldBase(className), 'resize-y leading-relaxed', className)}
        {...rest}
      />
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select
        ref={ref}
        className={cx(fieldBase(className), 'min-w-0 max-w-full cursor-pointer pr-10', className)}
        {...rest}
      >
        {children}
      </select>
    );
  },
);

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <label className="block space-y-2" htmlFor={htmlFor}>
      <span className="block text-sm font-medium">{label}</span>
      {children}
      {error ? (
        <span className="block text-sm text-red-600">{error}</span>
      ) : hint ? (
        <span className="block text-sm text-[var(--text-muted)]">{hint}</span>
      ) : null}
    </label>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="min-w-0">
        <div className="text-[0.95rem] font-medium">{label}</div>
        {description ? (
          <div className="mt-0.5 text-sm text-[var(--text-muted)]">{description}</div>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          'relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors duration-300 disabled:opacity-40',
          checked ? 'bg-clay-500' : 'bg-[var(--color-sand-300)] dark:bg-ink-700',
        )}
      >
        <motion.span
          layout
          transition={{ duration: DURATION.fast, ease: EASE_OUT_EXPO }}
          className={cx(
            'absolute top-0.5 size-5 rounded-full bg-white shadow-sm',
            checked ? 'left-[1.375rem]' : 'left-0.5',
          )}
        />
      </button>
    </div>
  );
}

// ------------------------------------------------------------------- Layout
export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'clay' | 'lagoon' | 'sun' | 'muted';
  className?: string;
}) {
  const tones = {
    neutral: 'bg-[var(--surface-sunken)] text-[var(--text-strong)]',
    clay: 'bg-clay-500/12 text-clay-700 dark:text-clay-300',
    lagoon: 'bg-lagoon-500/12 text-lagoon-600 dark:text-lagoon-300',
    sun: 'bg-sun-400/16 text-sun-500',
    muted: 'bg-transparent border border-[var(--border-subtle)] text-[var(--text-muted)]',
  } as const;
  return (
    <span
      className={cx(
        'inline-flex max-w-full items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Card({
  children,
  className,
  as: As = 'div',
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'article' | 'section' | 'li';
}) {
  return <As className={cx('surface-card p-5', className)}>{children}</As>;
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  align = 'left',
}: {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  align?: 'left' | 'center';
}) {
  return (
    <div className={cx('max-w-3xl', align === 'center' && 'mx-auto text-center')}>
      {eyebrow ? <div className="eyebrow mb-4">{eyebrow}</div> : null}
      <h2 className="display text-4xl sm:text-5xl lg:text-[3.5rem]">{title}</h2>
      {description ? (
        <p className="mt-5 text-lg leading-relaxed text-[var(--text-muted)]">{description}</p>
      ) : null}
    </div>
  );
}

export function EmptyState({
  icon = '🧭',
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--border-subtle)] px-6 py-16 text-center">
      <div className="mb-4 text-4xl">{icon}</div>
      <h3 className="text-lg font-semibold">{title}</h3>
      {description ? (
        <p className="mt-2 max-w-md text-sm text-[var(--text-muted)]">{description}</p>
      ) : null}
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  );
}

export function Alert({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'error' | 'success' | 'warning';
  children: ReactNode;
}) {
  const tones = {
    info: 'bg-lagoon-500/10 text-lagoon-600 dark:text-lagoon-300',
    error: 'bg-red-500/10 text-red-600 dark:text-red-400',
    success: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    warning: 'bg-sun-400/14 text-sun-500',
  } as const;
  return (
    <div className={cx('rounded-xl px-4 py-3 text-sm leading-relaxed', tones[tone])} role="alert">
      {children}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cx(
        'animate-pulse rounded-lg bg-[var(--surface-sunken)]',
        className,
      )}
    />
  );
}
